/**
 * Clerk session heartbeats (`/v1/client/sessions/:id/touch`) fail whenever Safari
 * or iOS aborts a background fetch ("Load failed") or the device is briefly
 * offline ("Failed to fetch"). ClerkJS surfaces those as unhandled errors;
 * they are not application bugs and should not enter PostHog Error Tracking.
 *
 * Keep user-initiated Clerk failures (sign-in, MFA, token exchange) so they
 * still appear as issues.
 */

const CLERK_SESSION_TOUCH_NETWORK_ERROR =
  /ClerkJS:\s*Network error at "[^"]*\/v1\/client\/sessions\/[^/"?]+\/touch/i;

const TRANSIENT_FETCH_FAILURE =
  /TypeError:\s*(Load failed|Failed to fetch)|NetworkError|The Internet connection appears to be offline|AbortError|The operation was aborted/i;

export interface PostHogCaptureEvent {
  event: string;
  properties?: Record<string, unknown>;
}

export function isUnactionableClerkSessionTouchError(message: string): boolean {
  return CLERK_SESSION_TOUCH_NETWORK_ERROR.test(message) && TRANSIENT_FETCH_FAILURE.test(message);
}

export function extractExceptionMessages(properties: Record<string, unknown> | undefined): string[] {
  if (!properties) return [];

  const messages: string[] = [];
  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    for (const value of values) {
      if (typeof value === "string") messages.push(value);
    }
  }

  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list) {
      if (!item || typeof item !== "object" || !("value" in item)) continue;
      const value = item.value;
      if (typeof value === "string") messages.push(value);
    }
  }

  const message = properties.$exception_message;
  if (typeof message === "string") messages.push(message);

  return messages;
}

export function shouldDropPostHogExceptionEvent(event: PostHogCaptureEvent | null): boolean {
  if (!event || event.event !== "$exception") return false;
  return extractExceptionMessages(event.properties).some(isUnactionableClerkSessionTouchError);
}

export function dropUnactionablePostHogExceptions<T extends PostHogCaptureEvent>(
  event: T | null,
): T | null {
  if (!event) return event;
  if (shouldDropPostHogExceptionEvent(event)) return null;
  return event;
}
